import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video as VideoIcon, Wifi } from "lucide-react";
import { genderForVoice } from "@/lib/voiceGender";

/**
 * Realistic interviewer avatar shown as a Zoom/Teams-style video tile.
 *
 * Props:
 *   state: 'idle' | 'speaking' | 'listening'
 *   audioEl: optional HTMLAudioElement — when provided in 'speaking' state, the
 *            border accent + waveform driven by real-time audio amplitude.
 *   voice: OpenAI TTS voice name → maps to female/male photo.
 */
export default function InterviewerAvatar({ state = "idle", audioEl = null, voice = "nova" }) {
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const waveRef = useRef(null);
  const ringRef = useRef(null);

  const [time, setTime] = useState(() => formatNow());
  useEffect(() => {
    const id = setInterval(() => setTime(formatNow()), 30_000);
    return () => clearInterval(id);
  }, []);

  const gender = genderForVoice(voice);
  const photo = gender === "female"
    ? "/interviewers/female.png"
    : "/interviewers/male.png";

  const persona = gender === "female"
    ? { name: "Sarah Mitchell", role: "Senior Interviewer" }
    : { name: "James Carter", role: "Senior Interviewer" };

  useEffect(() => {
    if (state !== "speaking" || !audioEl) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (waveRef.current) waveRef.current.style.setProperty("--amp", "0");
      if (ringRef.current) ringRef.current.style.setProperty("--glow", "0");
      return;
    }
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});

      if (!sourceRef.current || sourceRef.current.mediaElement !== audioEl) {
        try {
          sourceRef.current = ctx.createMediaElementSource(audioEl);
          analyserRef.current = ctx.createAnalyser();
          analyserRef.current.fftSize = 256;
          sourceRef.current.connect(analyserRef.current);
          analyserRef.current.connect(ctx.destination);
        } catch (e) {
          // already connected
        }
      }
      const analyser = analyserRef.current;
      if (!analyser) return;
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        const bins = Math.min(32, data.length);
        for (let i = 0; i < bins; i++) sum += data[i];
        const avg = sum / bins;
        const amp = Math.min(1, avg / 90);
        if (waveRef.current) waveRef.current.style.setProperty("--amp", amp.toFixed(3));
        if (ringRef.current) ringRef.current.style.setProperty("--glow", amp.toFixed(3));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      // ignore
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (waveRef.current) waveRef.current.style.setProperty("--amp", "0");
      if (ringRef.current) ringRef.current.style.setProperty("--glow", "0");
    };
  }, [state, audioEl]);

  const stateLabel = {
    idle: "Ready",
    speaking: "Speaking",
    listening: "Listening",
  }[state] || "Ready";

  return (
    <div
      className={`iv-tile iv-state-${state}`}
      data-testid="interviewer-avatar"
      data-gender={gender}
    >
      {/* Speaking ring glow (driven by audio amplitude) */}
      <div ref={ringRef} className="iv-tile-ring" aria-hidden="true" />

      {/* Photo + idle micro-animations */}
      <div className="iv-tile-frame">
        <img
          src={photo}
          alt={persona.name}
          className="iv-tile-photo"
          draggable={false}
        />
        {/* Subtle blink overlay (eyelid) */}
        <div className="iv-blink" aria-hidden="true" />
        {/* Mouth-area glow + motion when speaking — creates "sound coming from mouth" illusion */}
        <div ref={waveRef} className="iv-mouth-fx" aria-hidden="true">
          <div className="iv-mouth-glow" />
          <div className="iv-mouth-wave">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} style={{ "--i": i }} />
            ))}
          </div>
        </div>
        {/* Vignette + grain for cinematic feel */}
        <div className="iv-tile-vignette" aria-hidden="true" />

        {/* Top-right HD/Recording badge */}
        <div className="iv-badge-rec">
          <span className="iv-rec-dot" /> LIVE
        </div>

        {/* Bottom-left name plate (Zoom-style) */}
        <div className="iv-nameplate">
          <div className="flex items-center gap-1.5">
            <MicOff className="w-3 h-3 opacity-80" style={{ display: state === "listening" ? "none" : undefined }} />
            <Mic className="w-3 h-3" style={{ display: state === "listening" ? undefined : "none" }} />
            <span className="font-semibold leading-tight">{persona.name}</span>
          </div>
          <div className="text-[10px] opacity-80 mt-0.5">{persona.role}</div>
        </div>

        {/* Bottom-right HUD */}
        <div className="iv-hud">
          <VideoIcon className="w-3 h-3" />
          <span className="text-[10px] font-mono tabular-nums">HD</span>
          <Wifi className="w-3 h-3 ml-1" />
          <span className="text-[10px] font-mono tabular-nums">{time}</span>
        </div>
      </div>

      {/* State chip below tile */}
      <div className="iv-state-chip" data-testid="avatar-state">
        <span className={`iv-state-dot iv-state-dot-${state}`} />
        {stateLabel}
      </div>
    </div>
  );
}

function formatNow() {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
